import { message, Modal } from "antd";
import { isEmpty } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import SignatureCanvas from "react-signature-canvas";
import {
  AcceptQuickContractRequest,
  AwardToMyselfSaga,
  ContractAcceptRequest,
  UpdateContractRequest,
  UpdateContractStatusRequest,
} from "../../api/contractsApi/acceptRejectApi";
import { cancelContractRequest } from "../../api/contractsApi/cancelContractApi";
import {
  GenerateGigmiCharges,
  GetContractDetails,
} from "../../api/contractsApi/contractDetails";
import backIcon from "../../assets/images/backButton.svg";
import Disclaimer from "../../components/BankInfo/Disclaimer";
import { CheckInButton } from "../../components/Contracts/CheckInButton";
import { CompanyProfileCard } from "../../components/Contracts/CompanyProfileCard";
import ContractActions from "../../components/Contracts/ContractActions";
import ContractAmountDetailsModal from "../../components/Contracts/ContractAmountDetailModal";
import { ContractButtons } from "../../components/Contracts/ContractButtons";
import { ContractDatesCard } from "../../components/Contracts/ContractDatesCard";
import { ContractDetailCard } from "../../components/Contracts/ContractDetailCard";
import { RightIconGenerator } from "../../components/Contracts/ContractDetailRightIcons";
import { ContractDetailMainButton } from "../../components/Contracts/ContractDetailsMainButton";
import { ContractLogs } from "../../components/Contracts/ContractLogs";
import { ContractorProfileCard } from "../../components/Contracts/ContractorProfileCard";
import ContractToggleButton from "../../components/Contracts/ContractToggleButton";
import DisputeModal from "../../components/Contracts/DisputeModal";
import { formatDate } from "../../components/Contracts/helper";
import { HomeownerProfileCard } from "../../components/Contracts/HomeownerProfileCard";
import { ProofOfWorkCard } from "../../components/Contracts/ProofOfWorkCard";
import { TradesmanProfileCard } from "../../components/Contracts/TradesmanProfileCard";
import VerificationModal from "../../components/Contracts/VerificationModal";
import Loader from "../../components/GeneralComponents";
import Header from "../../components/Header/Header";
import {
  CLAIM_TYPE,
  capitalizeTo,
  disputeContractTotal,
  getContractedUser,
  getUserSkills,
  resolveClaimedAmount,
} from "../../utils/Helper";
import STRINGS from "../../utils/UserTypes";
import SubmitProofOfWorkModal from "./SubmitProofOfWork";
import { isStripe } from "../../utils/paymentProvider";
import { useStripeContractCharge } from "../../hooks/useStripeContractCharge";
import { useFeeBreakdown } from "../../hooks/useFeeBreakdown";
import PaymentElementModal from "../../components/Stripe/PaymentElementModal";
import ContractStatusBanner from "../../components/Contracts/ContractStatusBanner";
import {
  isContractPayer,
  isSubcontractAwaitingHold,
  isStandardContractAwaitingHold,
  getStatusInfoBody,
} from "../../components/Contracts/stripeContractHelpers";
import { useQuickContractPayeeOnboarding } from "../../hooks/useQuickContractPayeeOnboarding";
import { ROUTES } from "../../constants/routes";
const ContractDetails = ({ intl }) => {
  const signatureRef = useRef();
  const {
    chargeViaStripe,
    preparing: chargePreparing,
    modalProps: stripeChargeModalProps,
  } = useStripeContractCharge();
  const [modalVisible, setModalVisible] = useState(false);
  const [disputeModal, setDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [claimType, setClaimType] = useState(CLAIM_TYPE.FULL);
  const [claimedAmount, setClaimedAmount] = useState(null);
  const [claimError, setClaimError] = useState("");
  const [disputeSubmitting, setDisputeSubmitting] = useState(false);
  const [verificationModal, setVerificationModal] = useState(false);
  const [awardToSelfModal, setAwardToSelfModal] = useState(false);
  const [isGigsterAvailable, setIsGigsterAvailable] = useState(false);
  const [chargesModal, setChargesModal] = useState(false);
  const [confirmChargesModal, setConfirmChargesModal] = useState(false);
  const [signatureModal, setSignatureModal] = useState(false);
  const [paymentType, setPaymentType] = useState(null);
  const [accountInfo, setAccountInfo] = useState(false);
  const [contractState, setContractState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const dispatch = useDispatch();
  const { contractDetails: contractDetail } = useSelector(
    (state) => state?.contracts
  );
  const userTypeData = useSelector((state) => state?.common?.newUser?.data);
  const fullUser = useSelector((state) => state?.common?.fullUser);
  const gigmiCharges = useSelector((state) => state?.gigmiCharges?.charges);
  const userData = JSON.parse(localStorage.getItem("data"));
  const navigate = useNavigate();

  // G4: prompt a not-payout-ready quick-contract payee-sender to onboard, once.
  const { showPrompt: showPayeeOnboardPrompt, dismiss: dismissPayeeOnboard } =
    useQuickContractPayeeOnboarding(
      contractDetail,
      userData?.user_type,
      dispatch
    );

  // G2: Flow-C subcontract awaiting the payer's auth-hold → Pay-Now banner.
  const showSubcontractHoldBanner =
    isSubcontractAwaitingHold(contractDetail) &&
    isContractPayer(contractDetail, userData?.user_type);

  // Top-level standard contract awaiting the contractor's auth-hold → Pay-Now banner.
  const showStandardHoldBanner = isStandardContractAwaitingHold(
    contractDetail,
    userData?.user_type
  );

  // G12: waiting/blocked status explanation (Stripe only; read-only banner).
  const showStatusInfoBanner =
    isStripe() &&
    ["waiting", "blocked"].includes(contractDetail?.primary_status) &&
    !showSubcontractHoldBanner &&
    !showStandardHoldBanner;

  useEffect(() => {
    (async () => {
      setLoading(true);
      await GetContractDetails(id, dispatch);
      setLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    const userContractedType = getContractedUser(userData, contractDetail);

    if (userContractedType?.id) {
      (async () => {
        await getUserSkills(
          {
            receiverID: userContractedType?.id,
            receiver_type: userContractedType?.user_contracted_type,
          },
          dispatch,
          intl
        );
      })();
    }
  }, [contractDetail]);

  useEffect(() => {
    const handleFocus = () => {
      if (userData?.user_type !== "tradesman") {
        const body = {
          homeOwnerId:
            userData?.user_type === "contractor" ? null : userTypeData?.id,
          contractorId:
            userData?.user_type === "contractor" ? userTypeData?.id : null,
        };
        (async () => {
          await GenerateGigmiCharges(body, dispatch);
        })();
      }
      if (
        contractDetail?.secondary_status &&
        ((contractDetail?.contractor &&
          contractDetail?.homeowner &&
          (userData?.user_type === "homeowner" ||
            userData?.user_type === "contractor")) ||
          (contractDetail?.contractor &&
            contractDetail?.tradesman &&
            contractDetail?.parentContractId &&
            contractDetail?.updated_line_item?.length > 0 &&
            (userData?.user_type === "contractor" ||
              userData?.user_type === "tradesman")))
      ) {
        setContractState(true);
      }
    };
    handleFocus();
  }, []);

  const getCalculatedStartDate = () => {
    return !isEmpty(contractDetail?.dates_record) &&
      contractDetail?.dates_record?.length > 0
      ? contractDetail?.dates_record[contractDetail?.dates_record?.length - 1]
          ?.start_date
      : contractDetail?.dates_record[0]?.start_date;
  };

  const getCalculatedEndDate = useCallback(() => {
    return !contractState && contractDetail?.dates_record?.length > 0
      ? contractDetail?.dates_record[contractDetail?.dates_record?.length - 1]
          ?.end_date
      : contractDetail?.dates_record[0]?.end_date;
  }, [contractState, contractDetail]);

  const calculateFlatAmount = () => {
    const contractStatuses =
      contractDetail?.homeownerId && !contractDetail?.tradesmanId
        ? [
            "contractor_changed_requirements_homeowner",
            "homeowner_accepted_changes",
          ]
        : [
            "contractor_changed_requirements_gigster",
            "gigster_changed_requirements_homeowner",
            "gigster_accepted_changes",
          ];

    if (
      !contractState &&
      contractStatuses?.includes(contractDetail?.secondary_status)
    ) {
      const flatAmount = parseInt(contractDetail?.changed_flat_amount);
      return flatAmount || 0;
    } else if (contractDetail?.updated_flat_amount) {
      const flatAmount = parseInt(contractDetail?.updated_flat_amount);
      return flatAmount || 0;
    } else {
      const flatAmount = parseInt(contractDetail?.flat_amount);
      return flatAmount || 0;
    }
  };

  const gigMiFeeCalculations = useMemo(
    () =>
      userData.user_type === "homeowner" &&
      [
        "contractor_changed_requirements_homeowner",
        "gigster_changed_requirements_homeowner",
      ].includes(contractDetail?.secondary_status),
    [contractDetail?.secondary_status, userData.current_type]
  );

  // Server-resolved fee so the confirm-charges total equals the actual Stripe charge
  // (fee can be percent). Skips the fetch for the estimate-change path (fee forced to 0).
  const { breakdown: feeBreakdown } = useFeeBreakdown({
    contractId: contractDetail?.id,
    flatAmount: calculateFlatAmount(),
    legacyFee: gigmiCharges,
    enabled: (confirmChargesModal || chargesModal) && !gigMiFeeCalculations,
  });

  const getJustifyContent = (contractDetail) => {
    if (
      (contractDetail?.contractor && contractDetail?.tradesman) ||
      (contractDetail?.contractor && contractDetail?.homeowner) ||
      (contractDetail?.tradesman && contractDetail?.homeowner)
    ) {
      return "space-around";
    } else {
      return "center";
    }
  };

  const getReviews = () => {
    const reviews = [];

    if (contractDetail?.review?.length > 0) {
      contractDetail?.review?.forEach((element) => {
        if (element?.proof.length > 0) {
          reviews?.push(element);
        }
      });
    }
    return reviews;
  };

  const handleFlatAmountOnPress = async () => {
    let body = {};
    if (userData?.user_type !== STRINGS.TRADESMAN_TYPE) {
      if (userData?.user_type === STRINGS.HOMEOWNER_TYPE) {
        body = {
          homeOwnerId: userTypeData?.id,
        };
      } else if (contractDetail?.homeownerId) {
        return;
      } else {
        body = {
          contractorId: userTypeData?.id,
        };
      }
      setLoading(true);
      await GenerateGigmiCharges(body, dispatch);
      setLoading(false);
      setChargesModal(true);
    }
  };

  const resetDispute = () => {
    setDisputeModal(false);
    setClaimType(CLAIM_TYPE.FULL);
    setClaimedAmount(null);
    setClaimError("");
  };

  const onChangeClaimType = (nextType) => {
    setClaimType(nextType);
    setClaimError("");
  };

  const onChangeClaimedAmount = (nextAmount) => {
    setClaimedAmount(nextAmount);
    setClaimError("");
  };

  const handleDisputeModal = async () => {
    if (!disputeReason?.trim()?.length) {
      message.error(intl.formatMessage({ id: "PLEASE_ADD_DISPUTE_REASON" }));
      return;
    }

    const maxClaimAmount = disputeContractTotal(contractDetail);
    const { error, value } = resolveClaimedAmount(
      claimType,
      claimedAmount,
      maxClaimAmount
    );
    if (error) {
      setClaimError(
        intl.formatMessage({ id: error }, { amount: maxClaimAmount.toFixed(2) })
      );
      return;
    }

    const payload = {
      contract_Id: contractDetail?.id,
      disputeReason: disputeReason.trim(),
      claimedAmount: value,
      status: "disputed",
    };

    // The confirm control stays mounted for the whole round-trip, so without this
    // a second tap sends a second dispute for the same contract.
    if (disputeSubmitting) {
      return;
    }
    setDisputeSubmitting(true);
    try {
      await UpdateContractStatusRequest(payload, navigate, contractDetail, intl);
    } finally {
      setDisputeSubmitting(false);
    }
  };

  // Homeowner funding charge + status advance. Shared by the signature-Save path
  // (legacy/aeropay) and, under Stripe, called DIRECTLY from the confirm-charges
  // modal so the payer goes confirm → PaymentSheet with NO signature step —
  // matching mobile (`useContractCharge.handleContractCreation`, which has no
  // signature). `contractDetail?.secondary_status` (estimate-change acceptance)
  // is not a charge and is handled by the signature path only.
  const runFundingChargeAndAdvance = async () => {
    if (contractDetail?.status === "direct_pay_to_" + userData?.user_type) {
      // Real charge (quick-contract funding): confirm via Element first.
      if (isStripe() && !(await chargeViaStripe(contractDetail?.id))) {
        return;
      }
      setLoading(true);
      await AcceptQuickContractRequest(
        {
          contractId: contractDetail?.id,
          status: "accept",
        },
        navigate
      );
      setLoading(false);
    } else {
      // Real charge (homeowner ASSIGNED funding): confirm via Element first.
      if (isStripe() && !(await chargeViaStripe(contractDetail?.id))) {
        return;
      }
      setLoading(true);
      const payload = { contract_Id: contractDetail?.id, status: "assigned" };
      await UpdateContractStatusRequest(
        payload,
        navigate,
        contractDetail,
        intl
      );
      setLoading(false);
    }
  };

  // G7: the estimate-change acceptance transition, extracted so it can run directly
  // under Stripe (no signature) — the same UpdateContractRequest the signature path
  // makes for a homeowner accepting changed requirements.
  const applyEstimateChangeAcceptance = async () => {
    setLoading(true);
    await UpdateContractRequest(
      {
        contract_Id: contractDetail?.id,
        secondary_status: [
          "contractor_changed_requirements_homeowner",
          "gigster_changed_requirements_homeowner",
        ].includes(contractDetail?.secondary_status)
          ? "homeowner_accepted_changes"
          : undefined,
        isSubContract: false,
        contractDetail: contractDetail,
      },
      navigate
    );
    setLoading(false);
  };

  const handleSaveSignature = async () => {
    const signatureDataURL = signatureRef.current?.toDataURL();
    const base64Signature = signatureDataURL.split(",")[1];
    if (base64Signature) {
      setSignatureModal(false);
      if (contractDetail?.secondary_status) {
        await UpdateContractRequest(
          {
            contract_Id: contractDetail?.id,
            secondary_status: [
              "contractor_changed_requirements_homeowner",
              "gigster_changed_requirements_homeowner",
            ].includes(contractDetail?.secondary_status)
              ? "homeowner_accepted_changes"
              : undefined,
            isSubContract: false,
            contractDetail: contractDetail,
          },
          navigate
        );
      } else {
        await runFundingChargeAndAdvance();
      }
    }
  };

  const handleClearSignature = () => {
    signatureRef.current?.clear();
  };

  return (
    <>
      {(loading || chargePreparing) && (
        <div className="search-loader">
          <Loader />
        </div>
      )}

      <div className="container">
        <Header />
        <div className="paymentscreen-inner">
          <div className="d-flex justify-content-between align-items-center pb-3">
            <span className="d-flex align-items-center">
              <img
                src={backIcon}
                alt=""
                onClick={() => navigate(-1)}
                style={{ cursor: "pointer" }}
                className="shadow-lg border rounded"
              />
              <span className="text-black fs-4 m-1">
                <FormattedMessage id="back" />
              </span>
            </span>
            <span className="fw-bold fs-5">
              <FormattedMessage id="contract-details" />
            </span>
            <span>
              <RightIconGenerator
                contractDetail={contractDetail}
                userData={userData}
              />
            </span>
          </div>

          <CompanyProfileCard contractDetail={contractDetail} />
          <div
            style={{
              display: "flex",
              justifyContent: getJustifyContent(contractDetail),
            }}
            className="w-100"
          >
            <ContractorProfileCard
              contractDetail={contractDetail}
              userData={userData}
              userTypeData={userTypeData}
            />
            <HomeownerProfileCard
              contractDetail={contractDetail}
              userData={userData}
              userTypeData={userTypeData}
            />
            <TradesmanProfileCard
              contractDetail={contractDetail}
              userData={userData}
              userTypeData={userTypeData}
            />
          </div>
          <ContractToggleButton
            contractDetail={contractDetail}
            userData={userData}
            contractState={contractState}
            setContractState={setContractState}
          />
          {!isEmpty(contractDetail?.dates_record) && (
            <ContractDatesCard
              startDate={getCalculatedStartDate()}
              endDate={getCalculatedEndDate()}
            />
          )}
          <div className="d-flex justify-content-center w-100 mt-3">
            <span className="fw-bold text-center mt-3">
              <FormattedMessage id="created_at" />{" "}
              {formatDate(contractDetail?.created_at)}
            </span>
          </div>
          {contractDetail && (
            <ContractDetailCard
              contractDetails={contractDetail}
              showDescription={true}
              contractState={contractState}
              flatAmountOnPress={handleFlatAmountOnPress}
              contract_location={
                contractDetail?.contract_location &&
                contractDetail?.contract_location[0]
              }
              userData={userData}
            />
          )}
          {showSubcontractHoldBanner && (
            <ContractStatusBanner
              testId="contract-details-subcontract-hold-banner"
              messageId="SUBCONTRACT_HOLD_PENDING_BANNER"
              actionTextId="PAY_NOW"
              loading={chargePreparing}
              onAction={async () => {
                // Re-run the auth-hold sheet; on success refetch so is_paid/status
                // refresh in place (the webhook also clears WAITING server-side).
                const paid = await chargeViaStripe(contractDetail?.id);
                if (paid) {
                  await GetContractDetails(id, dispatch);
                }
              }}
            />
          )}
          {showStandardHoldBanner && (
            <ContractStatusBanner
              testId="contract-details-standard-hold-banner"
              messageId="CONTRACT_HOLD_PENDING_BANNER"
              actionTextId="PAY_NOW"
              loading={chargePreparing}
              onAction={async () => {
                const paid = await chargeViaStripe(contractDetail?.id);
                if (paid) {
                  await GetContractDetails(id, dispatch);
                }
              }}
            />
          )}
          {showStatusInfoBanner && (
            <ContractStatusBanner
              testId="contract-details-status-info-banner"
              messageId={getStatusInfoBody(contractDetail, userData?.user_type)}
            />
          )}
          <CheckInButton
            userData={userData}
            contractLogs={contractDetail?.contract_logs}
            status={contractDetail?.status}
            contractDetail={contractDetail}
            setLoading={setLoading}
          />
          <ContractLogs contractLogs={contractDetail?.contract_logs} />
          {contractDetail?.review?.length > 0 && (
            <ProofOfWorkCard contractDetail={contractDetail} />
          )}
          <ContractDetailMainButton
            userData={userData}
            contractDetail={contractDetail}
            userTypeData={fullUser}
            dispatch={dispatch}
            setConfirmChargesModal={setConfirmChargesModal}
            setPaymentType={setPaymentType}
            setAccountInfo={setAccountInfo}
            setLoading={setLoading}
            setSignatureModal={setSignatureModal}
          />
          <ContractActions
            intl={intl}
            userData={userData}
            contractDetail={contractDetail}
            dispatch={dispatch}
            setSignatureModal={setSignatureModal}
            userTypeData={userTypeData}
            fullUser={fullUser}
            setConfirmChargesModal={setConfirmChargesModal}
            setPaymentType={setPaymentType}
            setAccountInfo={setAccountInfo}
            setLoading={setLoading}
          />

          <ContractButtons
            userData={userData}
            contractDetail={contractDetail}
            setCancelModal={setCancelModal}
            setDisputeModal={setDisputeModal}
            setModalVisible={setModalVisible}
            setAwardToSelfModal={setAwardToSelfModal}
            reviews={getReviews()}
          />
        </div>
        <SubmitProofOfWorkModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
        />
        <DisputeModal
          visible={disputeModal}
          title={<FormattedMessage id="DISPUTE" />}
          setReason={setDisputeReason}
          description={<FormattedMessage id="PROVIDE_DISPUTE_DETAILS" />}
          cancelAction={resetDispute}
          confirmAction={handleDisputeModal}
          cancelText={<FormattedMessage id="CANCEL" />}
          confirmText={<FormattedMessage id="submit" />}
          claimType={claimType}
          setClaimType={onChangeClaimType}
          claimedAmount={claimedAmount}
          setClaimedAmount={onChangeClaimedAmount}
          maxClaimAmount={disputeContractTotal(contractDetail)}
          claimError={claimError}
          confirmDisabled={disputeSubmitting}
        />
        <VerificationModal
          visible={verificationModal}
          onClose={() => setVerificationModal(false)}
          userTypeData={userTypeData}
          userData={userData}
        />
        <ContractAmountDetailsModal
          visible={confirmChargesModal}
          title={<FormattedMessage id="CONTRACT_AMOUNT_DETAIL" />}
          contractId={contractDetail?.id}
          gigmiCharges={gigMiFeeCalculations ? 0 : gigmiCharges}
          flatAmount={calculateFlatAmount()}
          breakdown={gigMiFeeCalculations ? undefined : feeBreakdown}
          useServerFee={isStripe() && !gigMiFeeCalculations}
          cancelText={<FormattedMessage id="CANCEL" />}
          cancelAction={() => setConfirmChargesModal(false)}
          confirmText={<FormattedMessage id="CONFIRM_CAPITALS" />}
          confirmAction={async () => {
            setConfirmChargesModal(false);
            const isHomeownerFundingCharge =
              userData.user_type === "homeowner" &&
              [
                "estimation_given",
                "direct_pay_to_" + userData?.user_type,
                "contract_given",
              ].includes(contractDetail?.status);
            const isHomeownerAcceptChanges =
              userData.user_type === "homeowner" &&
              [
                "contractor_changed_requirements_homeowner",
                "gigster_changed_requirements_homeowner",
              ].includes(contractDetail?.secondary_status);
            if (isHomeownerFundingCharge) {
              // Under Stripe: skip the (legacy) signature step and charge
              // directly, matching mobile. Aeropay keeps the signature.
              if (isStripe()) {
                await runFundingChargeAndAdvance();
              } else {
                setSignatureModal(true);
              }
            } else if (isHomeownerAcceptChanges) {
              // Estimate-change acceptance (not a charge). Under Stripe there is no
              // signature step anywhere (mobile parity) — apply the accepted-changes
              // transition directly. Aeropay keeps the signed record.
              if (isStripe()) {
                await applyEstimateChangeAcceptance();
              } else {
                setSignatureModal(true);
              }
            } else if (
              contractDetail?.status ===
              "direct_pay_to_" + userData?.user_type
            ) {
              // Real charge: confirm payment via the Element first; advance only
              // if it succeeded (R1/SEC-8). Legacy aeropay path is unchanged.
              if (isStripe() && !(await chargeViaStripe(contractDetail?.id))) {
                return;
              }
              setLoading(true);
              await AcceptQuickContractRequest(
                {
                  contractId: contractDetail?.id,
                  status: "accept",
                },
                navigate
              );
              setLoading(false);
            } else {
              // Payee/non-payer acceptance — no money moves from the actor, so
              // no Element (matches mobile's do-not-guard ContractAccept).
              const body = {
                contract_Id: contractDetail?.id,
                contractDetail,
              };
              setLoading(true);
              try {
                const accepted = await ContractAcceptRequest(
                  body,
                  dispatch,
                  navigate
                );
                // A contract the gigster sent takes the payer's money at accept
                // rather than holding it on send, so the server asks for payment
                // first. Charge, then accept again (GIG-132).
                if (accepted?.paymentRequired) {
                  const paid = await chargeViaStripe(contractDetail?.id);
                  if (!paid) {
                    message.error(
                      intl.formatMessage({
                        id: "PAYMENT_CANCELLED_NOT_ACCEPTED",
                      })
                    );
                  } else if (
                    (await ContractAcceptRequest(body, dispatch, navigate))
                      ?.paymentRequired
                  ) {
                    // Charged, but the server still cannot see it — the payments
                    // row is webhook-written and may lag. Never leave a payer who
                    // has just been charged looking at a screen that did nothing.
                    message.error(
                      intl.formatMessage({ id: "PAYMENT_CONFIRMING_TRY_AGAIN" })
                    );
                  }
                }
              } finally {
                setLoading(false);
              }
            }
          }}
        />
        <ContractAmountDetailsModal
          visible={chargesModal}
          title={<FormattedMessage id="CONTRACT_AMOUNT_DETAIL" />}
          contractId={contractDetail?.id}
          gigmiCharges={gigMiFeeCalculations ? 0 : gigmiCharges}
          flatAmount={calculateFlatAmount()}
          breakdown={gigMiFeeCalculations ? undefined : feeBreakdown}
          useServerFee={isStripe() && !gigMiFeeCalculations}
          cancelText={<FormattedMessage id="CANCEL" />}
          cancelAction={() => setChargesModal(false)}
        />
        <PaymentElementModal
          {...stripeChargeModalProps}
          title={<FormattedMessage id="CONTRACT_AMOUNT_DETAIL" />}
        />
        <Modal
          open={signatureModal}
          footer={null}
          onCancel={() => setSignatureModal(false)}
        >
          <div className="sig-heading">Add Signature</div>
          <div style={{ border: "3px solid #F77E0B", borderRadius: "5px" }}>
            <SignatureCanvas
              ref={signatureRef}
              canvasProps={{ width: 400, height: 200, className: "sigCanvas" }}
            />
          </div>
          <div className="d-flex justify-content-around mt-3">
            <button onClick={handleSaveSignature} className="button-signature">
              Save
            </button>
            <button onClick={handleClearSignature} className="button-signature">
              Clear
            </button>
          </div>
        </Modal>
        <Disclaimer
          showModal={awardToSelfModal}
          title={<FormattedMessage id="Please Verify" />}
          body={
            <FormattedMessage id="This contract will be awarded to your Gigster profile on this account. Do you want to continue?" />
          }
          cancelText={<FormattedMessage id="cancel" />}
          confirmText={<FormattedMessage id="CONFIRM" />}
          handleCancel={() => setAwardToSelfModal(false)}
          handleClick={async () => {
            setAwardToSelfModal(false);
            setLoading(true);
            if (
              fullUser?.availabilityRecord?.includes(STRINGS.TRADESMAN_TYPE)
            ) {
              await AwardToMyselfSaga(
                { contract_Id: contractDetail?.id },
                dispatch,
                navigate
              );
            } else {
              setIsGigsterAvailable(true);
            }
            setLoading(false);
          }}
        />
        <Disclaimer
          showModal={isGigsterAvailable}
          title={<FormattedMessage id="Please Hold On" />}
          body={
            <FormattedMessage id="Gigster profile is not Linked to this account. Please create Gigster profile before continuing." />
          }
          cancelText={<FormattedMessage id="cancel" />}
          handleCancel={() => setIsGigsterAvailable(false)}
        />
        <Disclaimer
          showModal={cancelModal}
          body={
            <FormattedMessage id="Are you sure you want to cancel this contract" />
          }
          cancelText={<FormattedMessage id="cancel" />}
          confirmText={<FormattedMessage id="CONFIRM" />}
          handleClick={async () => {
            setLoading(true);
            setCancelModal(false);
            await cancelContractRequest(
              {
                contractId: contractDetail?.id || null,
                contractCancelBy: userData?.user_type,
              },
              navigate
            );
            setLoading(false);
          }}
          handleCancel={() => setCancelModal(false)}
        />
        <Disclaimer
          showModal={accountInfo}
          setShowModal={() => setAccountInfo(false)}
          body={capitalizeTo(
            `${userData?.user_type} ${intl.formatMessage({
              id: "needs_to_add",
            })} ${paymentType} ${intl.formatMessage({
              id: "account_information",
            })}`
          )}
          cancelText={<FormattedMessage id="no" />}
          confirmText={<FormattedMessage id="yes" />}
          handleClick={() => {
            setAccountInfo(false);

            navigate("/get-payments");
          }}
          handleCancel={() => {
            setAccountInfo(false);
          }}
        />
        <Disclaimer
          showModal={showPayeeOnboardPrompt}
          title={<FormattedMessage id="QC_PAYEE_ONBOARD_TITLE" />}
          body={<FormattedMessage id="QC_PAYEE_ONBOARD_BODY" />}
          cancelText={<FormattedMessage id="cancel" />}
          confirmText={<FormattedMessage id="PAYOUTS_SET_UP_BUTTON" />}
          handleClick={() => {
            dismissPayeeOnboard();
            navigate(ROUTES.STRIPE_ONBOARDING);
          }}
          handleCancel={dismissPayeeOnboard}
        />
      </div>
    </>
  );
};

export default injectIntl(ContractDetails);
